import api from '../lib/api';

const auctionService = {
    // Auction endpoints
    getAllAuctions: async (params) => {
        try {
            const response = await api.get('/auctions', { params });
            return response.data;
        } catch (error) {
            console.error('Error fetching auctions:', error);
            throw error;
        }
    },

    getLiveAuctions: async (params) => {
        try {
            const response = await api.get('/live-auctions', { params });
            return response.data;
        } catch (error) {
            console.error('Error fetching live auctions:', error);
            throw error;
        }
    },

    getUpcomingActiveAuctions: async (params) => {
        try {
            const response = await api.get('/upcoming-active-auctions', { params });
            return response.data;
        } catch (error) {
            console.error('Error fetching upcoming active auctions:', error);
            throw error;
        }
    },

    getAuction: async (slug) => {
        try {
            const response = await api.get(`/auctions/${slug}`);
            return response.data;
        } catch (error) {
            console.error('Error fetching auction:', error);
            throw error;
        }
    },

    getLiveAuctionDetails: async (slug) => {
        try {
            const response = await api.get(`/auctions/${slug}/live`);
            return response.data;
        } catch (error) {
            console.error('Error fetching auction:', error);
            throw error;
        }
    },

    createAuction: async (auctionData) => {
        try {
            const response = await api.post('/auctions', auctionData);
            return response.data;
        } catch (error) {
            console.error('Error creating auction:', error);
            throw error;
        }
    },

    updateAuction: async (slug, auctionData) => {
        try {
            const response = await api.put(`/auctions/${slug}`, auctionData);
            return response.data;
        } catch (error) {
            console.error('Error updating auction:', error);
            throw error;
        }
    },

    deleteAuction: async (slug) => {
        try {
            const response = await api.delete(`/auctions/${slug}`);
            return response.data;
        } catch (error) {
            console.error('Error deleting auction:', error);
            throw error;
        }
    },

    startAuction: async (slug) => {
        try {
            const response = await api.post(`/auctions/${slug}/start`);
            return response.data;
        } catch (error) {
            console.error('Error starting auction:', error);
            throw error;
        }
    },

    endAuction: async (slug) => {
        try {
            const response = await api.post(`/auctions/${slug}/end`);
            return response.data;
        } catch (error) {
            console.error('Error ending auction:', error);
            throw error;
        }
    },

    cancelAuction: async (slug) => {
        try {
            const response = await api.post(`/auctions/${slug}/cancel`);
            return response.data;
        } catch (error) {
            console.error('Error cancelling auction:', error);
            throw error;
        }
    },

    duplicateAuction: async (slug, data = {}) => {
        try {
            const response = await api.post(`/auctions/${slug}/duplicate`, data);
            return response.data;
        } catch (error) {
            console.error('Error duplicating auction:', error);
            throw error;
        }
    },

    broadcastAnnouncement: async (auctionSlug, message, readAloud = false) => {
        try {
            const response = await api.post(`/auctions/${auctionSlug}/announcement`, { 
                message, 
                read_aloud: readAloud
            });
            return response.data;
        } catch (error) {
            console.error('Error broadcasting announcement:', error);
            throw error;
        }
    },

    broadcastSound: async (auctionSlug, soundFile) => {
        try {
            const response = await api.post(`/auctions/${auctionSlug}/sound`, { 
                sound_file: soundFile
            });
            return response.data;
        } catch (error) {
            console.error('Error broadcasting sound:', error);
            throw error;
        }
    },

    updateLiveStream: async (auctionSlug, liveStreamUrl) => {
        try {
            const response = await api.post(`/auctions/${auctionSlug}/live-stream`, {
                live_stream_url: liveStreamUrl || null
            });
            return response.data;
        } catch (error) {
            console.error('Error updating live stream:', error);
            throw error;
        }
    },

    getAuctionStatistics: async (auctionSlug) => {
        try {
            const response = await api.get(`/auctions/${auctionSlug}/statistics`);
            return response.data;
        } catch (error) {
            console.error('Error fetching auction statistics:', error);
            throw error;
        }
    },

    getAuctionBidActivity: async (auctionSlug) => {
        try {
            const response = await api.get(`/auctions/${auctionSlug}/bid-activity`);
            return response.data;
        } catch (error) {
            console.error('Error fetching auction bid activity:', error);
            throw error;
        }
    },

    // Lot endpoints
    getLots: async (auctionSlug, params = {}) => {
        try {
            const response = await api.get(`/auctions/${auctionSlug}/lots`, { params });
            return {
                data: response.data.data || response.data,
                pagination: response.data.pagination || null
            };
        } catch (error) {
            console.error('Error fetching lots:', error);
            throw error;
        }
    },

    browseLots: async (params) => {
        try {
            const response = await api.get('/browse-lots', { params });
            return response.data;
        } catch (error) {
            console.error('Error browsing lots:', error);
            throw error;
        }
    },

    getFeaturedLots: async (params = {}) => {
        try {
            const response = await api.get('/featured-lots', { params });
            return response.data;
        } catch (error) {
            console.error('Error fetching featured lots:', error);
            throw error;
        }
    },

    getLot: async (auctionSlug, lotSlug) => {
        try {
            const response = await api.get(`/auctions/${auctionSlug}/lots/${lotSlug}`);
            return response.data;
        } catch (error) {
            console.error('Error fetching lot:', error);
            throw error;
        }
    },

    createLot: async (auctionSlug, lotData) => {
        try {
            const response = await api.post(`/auctions/${auctionSlug}/lots`, lotData);
            return response.data;
        } catch (error) {
            console.error('Error creating lot:', error);
            throw error;
        }
    },

    duplicateLot: async (auctionSlug, lotSlug, data = {}) => {
        try {
            const response = await api.post(`/auctions/${auctionSlug}/lots/${lotSlug}/duplicate`, data);
            return response.data;
        } catch (error) {
            console.error('Error duplicating lot:', error);
            throw error;
        }
    },

    bulkUploadLots: async (auctionSlug, csvData) => {
        try {
            const response = await api.post(`/auctions/${auctionSlug}/lots/bulk-upload`, {
                csv_data: csvData
            });
            return response.data;
        } catch (error) {
            console.error('Error bulk uploading lots:', error);
            throw error;
        }
    },

    updateLot: async (auctionSlug, lotSlug, lotData) => {
        try {
            const response = await api.put(`/auctions/${auctionSlug}/lots/${lotSlug}`, lotData);
            return response.data;
        } catch (error) {
            console.error('Error updating lot:', error);
            throw error;
        }
    },

    deleteLot: async (auctionSlug, lotSlug) => {
        try {
            const response = await api.delete(`/auctions/${auctionSlug}/lots/${lotSlug}`);
            return response.data;
        } catch (error) {
            console.error('Error deleting lot:', error);
            throw error;
        }
    },

    activateLot: async (auctionSlug, lotSlug) => {
        try {
            const response = await api.post(`/auctions/${auctionSlug}/lots/${lotSlug}/activate`);
            return response.data;
        } catch (error) {
            console.error('Error activating lot:', error);
            throw error;
        }
    },

    getCurrentPrice: async (auctionSlug, lotSlug) => {
        try {
            const response = await api.get(`/auctions/${auctionSlug}/lots/${lotSlug}/current-price`);
            return response.data;
        } catch (error) {
            console.error('Error fetching current price:', error);
            throw error;
        }
    },

    getBidHistory: async (auctionSlug, lotSlug) => {
        try {
            const response = await api.get(`/auctions/${auctionSlug}/lots/${lotSlug}/bid-history`);
            return response.data;
        } catch (error) {
            console.error('Error fetching bid history:', error);
            throw error;
        }
    },

    // Bid endpoints
    placeBid: async (auctionSlug, lotSlug, bidData) => {
        try {
            const response = await api.post(`/auctions/${auctionSlug}/lots/${lotSlug}/bids`, bidData);
            return response.data;
        } catch (error) {
            console.error('Error placing bid:', error);
            throw error;
        }
    },

    getMyBids: async () => {
        try {
            const response = await api.get('/my-bids');
            return response.data;
        } catch (error) {
            console.error('Error fetching my bids:', error);
            throw error;
        }
    },

    getWinningBids: async () => {
        try {
            const response = await api.get('/winning-bids');
            return response.data;
        } catch (error) {
            console.error('Error fetching winning bids:', error);
            throw error;
        }
    },

    // Category endpoints
    getCategories: async () => {
        try {
            const response = await api.get('/categories');
            return response.data;
        } catch (error) {
            console.error('Error fetching categories:', error);
            throw error;
        }
    },

    getCategoriesWithCounts: async () => {
        try {
            const response = await api.get('/categories/with-counts');
            return response.data;
        } catch (error) {
            console.error('Error fetching categories with counts:', error);
            throw error;
        }
    },

    getCategory: async (id) => {
        try {
            const response = await api.get(`/categories/${id}`);
            return response.data;
        } catch (error) {
            console.error('Error fetching category:', error);
            throw error;
        }
    },

    createCategory: async (categoryData) => {
        try {
            const response = await api.post('/categories', categoryData);
            return response.data;
        } catch (error) {
            console.error('Error creating category:', error);
            throw error;
        }
    },

    updateCategory: async (id, categoryData) => {
        try {
            const response = await api.put(`/categories/${id}`, categoryData);
            return response.data;
        } catch (error) {
            console.error('Error updating category:', error);
            throw error;
        }
    },

    deleteCategory: async (id) => {
        try {
            const response = await api.delete(`/categories/${id}`);
            return response.data;
        } catch (error) {
            console.error('Error deleting category:', error);
            throw error;
        }
    },

    // User's auctions and lots
    getMyAuctions: async (params = {}) => {
        try {
            const response = await api.get('/my-auctions', { params });
            return response.data;
        } catch (error) {
            console.error('Error fetching user auctions:', error);
            throw error;
        }
    },

    getAuctionsForDropdown: async () => {
        try {
            const response = await api.get('/auctions-for-dropdown');
            console.log('Raw API response:', response);
            return {
                status: 'success',
                data: response.data?.data || []
            };
        } catch (error) {
            console.error('Error fetching auctions for dropdown:', error);
            throw error;
        }
    },

    getAuctionsForDropdownActive: async () => {
        try {
            const response = await api.get('/auctions-for-dropdown-active');
            console.log('Raw API response:', response);
            return {
                status: 'success',
                data: response.data?.data || []
            };
        } catch (error) {
            console.error('Error fetching active auctions for dropdown:', error);
            throw error;
        }
    },

    getMyLots: async (params = {}) => {
        return api.get('/my-lots', { params })
            .then(response => response.data)
            .catch(error => {
                console.error('Error fetching my lots:', error);
                throw error;
            });
    },

    getStats: async () => {
        try {
            const response = await api.get('/dashboard/stats');
            return response.data;
        } catch (error) {
            console.error('Error fetching stats:', error);
            throw error;
        }
    },

    getAnalytics: async () => {
        try {
            const response = await api.get('/dashboard/analytics');
            return response.data;
        } catch (error) {
            console.error('Error fetching analytics:', error);
            throw error;
        }
    },

    getRecentActivity: async () => {
        try {
            const response = await api.get('/dashboard/recent-activity');
            return response.data;
        } catch (error) {
            console.error('Error fetching recent activity:', error);
            throw error;
        }
    },

    toggleLotStatus: (auctionSlug, lotSlug) => api.post(`/auctions/${auctionSlug}/lots/${lotSlug}/activate`),

    buyNow: async (auctionSlug, lotSlug) => {
        try {
            const response = await api.post(`/auctions/${auctionSlug}/lots/${lotSlug}/buy-now`);
            return response.data;
        } catch (error) {
            console.error('Error buying now:', error);
            throw error;
        }
    },

    closeLot: async (auctionSlug, lotSlug) => {
        try {
            const response = await api.post(`/auctions/${auctionSlug}/lots/${lotSlug}/close`);
            return response.data;
        } catch (error) {
            console.error('Error closing lot:', error);
            throw error;
        }
    },

    timeoutLot: async (auctionSlug, lotSlug) => {
        try {
            const response = await api.post(`/auctions/${auctionSlug}/lots/${lotSlug}/timeout`);
            return response.data;
        } catch (error) {
            console.error('Error timing out lot:', error);
            throw error;
        }
    },

    placeVendorBid: async (auctionSlug, lotSlug) => {
        try {
            const response = await api.post(`/auctions/${auctionSlug}/lots/${lotSlug}/place-vendor-bid`);
            return response.data;
        } catch (error) {
            console.error('Error placing vendor bid:', error);
            throw error;
        }
    },

    // Image upload methods
    uploadAuctionImages(auctionSlug, formData, onUploadProgress) {
        return api.post(`/auctions/${auctionSlug}/images`, formData, {
            headers: {
                'Content-Type': 'multipart/form-data'
            },
            onUploadProgress: onUploadProgress || (() => {})
        });
    },

    uploadLotImages(auctionSlug, lotSlug, formData, onUploadProgress) {
        return api.post(`/auctions/${auctionSlug}/lots/${lotSlug}/images`, formData, {
            headers: {
                'Content-Type': 'multipart/form-data'
            },
            onUploadProgress: onUploadProgress || (() => {})
        });
    },

    deleteAuctionImage(auctionSlug, imageId) {
        return api.delete(`/auctions/${auctionSlug}/images/${imageId}`);
    },

    deleteLotImage(auctionSlug, lotSlug, imageId) {
        return api.delete(`/auctions/${auctionSlug}/lots/${lotSlug}/images/${imageId}`);
    },

    getAuctionImages(auctionSlug) {
        return api.get(`/auctions/${auctionSlug}/images`);
    },

    getLotImages(auctionSlug, lotSlug) {
        return api.get(`/auctions/${auctionSlug}/lots/${lotSlug}/images`);
    },

    // Generic entity methods
    getEntity: async (entityType, entitySlug) => {
        const endpoint = entityType === 'lot' ? 'lots' : 'auctions';
        return await api.get(`/${endpoint}/${entitySlug}`);
    },

    // Generic image methods
    uploadImages: async (formData) => {
        return await api.post('/images', formData, {
            headers: {
                'Content-Type': 'multipart/form-data',
            },
        });
    },

    deleteImage: async (entityType, entitySlug, imageId) => {
        try {
            const response = await api.delete(`/images/${imageId}`);
            return response.data;
        } catch (error) {
            console.error('Error deleting image:', error);
            throw error;
        }
    },

    respondToInvitation: async (auctionSlug, data) => {
        const response = await api.post(`/auctions/${auctionSlug}/participants/respond`, data);
        return response.data;
    },

    getPendingInvitations: async () => {
        const response = await api.get('/auctions/invitations/pending');
        return response.data;
    },

    getAcceptedInvitations: async () => {
        const response = await api.get('/auctions/invitations/accepted');
        return response.data;
    },

    inviteParticipant: async (auctionSlug, data) => {
        const response = await api.post(`/auctions/${auctionSlug}/participants/invite`, data);
        return response.data;
    },

    bulkUploadUsers: async (auctionSlug, csvData) => {
        const response = await api.post(`/auctions/${auctionSlug}/participants/bulk-upload`, {
            csv_data: csvData
        });
        return response.data;
    },

    generateDirectLink: async (auctionSlug, count = 1) => {
        const response = await api.post(`/auctions/${auctionSlug}/participants/direct-link`, { count });
        return response.data;
    },

    removeParticipant: async (auctionSlug, participantId) => {
        const response = await api.delete(`/auctions/${auctionSlug}/participants/${participantId}`);
        return response.data;
    },

    getParticipants: async (auctionSlug) => {
        const response = await api.get(`/auctions/${auctionSlug}/participants`);
        return response.data;
    },

    checkInvitationStatus: async (slug) => {
        try {
            const response = await api.get(`/auctions/${slug}/invitation-status`);
            return response.data;
        } catch (error) {
            console.error('Error checking invitation status:', error);
            throw error;
        }
    },

    getAuctionDetails: async (slug) => {
        const response = await api.get(`/auctions/${slug}`);
        return response.data;
    },

    getAuctionDetailsWithInvitation: async (slug, invitationCode) => {
        const response = await api.get(`/auctions/${slug}/invitation/${invitationCode}`);
        return response.data;
    },

    getSentInvitations: async () => {
        const response = await api.get('/auctions/participants/sent-invitations');
        console.log('API Response:', response); // Debug log
        return response.data;
    },

    updateLotsOrder: async (auctionId, lotIds) => {
        try {
            const response = await api.post(`/auctions/${auctionId}/lots/reorder`, { lot_ids: lotIds });
            return response.data;
        } catch (error) {
            console.error('Error updating lots order:', error);
            throw error;
        }
    },

    // Admin auction management
    getAdminAuctions: async (params) => {
        try {
            const response = await api.get('/admin/auctions', { params });
            return response.data;
        } catch (error) {
            console.error('Error fetching admin auctions:', error);
            throw error;
        }
    },

    updateAdminAuctionStatus: async (slug, status, additionalData = {}) => {
        try {
            const requestData = { status, ...additionalData };
            const response = await api.put(`/admin/auctions/${slug}/status`, requestData);
            return response.data;
        } catch (error) {
            console.error('Error updating admin auction status:', error);
            throw error;
        }
    },

    deleteAdminAuction: async (slug) => {
        try {
            const response = await api.delete(`/admin/auctions/${slug}`);
            return response.data;
        } catch (error) {
            console.error('Error deleting admin auction:', error);
            throw error;
        }
    },

    resetAdminAuction: async (slug) => {
        try {
            const response = await api.post(`/admin/auctions/${slug}/reset`);
            return response.data;
        } catch (error) {
            console.error('Error resetting admin auction:', error);
            throw error;
        }
    },

    // Admin: get last 3 bidders for each lot of a completed auction
    getAdminAuctionLastBidders: async (slug) => {
        try {
            const response = await api.get(`/admin/auctions/${slug}/last-bidders`);
            return response.data;
        } catch (error) {
            console.error('Error fetching last bidders:', error);
            throw error;
        }
    },

    // Admin: get all lots for an auction
    getAdminAuctionLots: async (auctionSlug) => {
        try {
            const response = await api.get(`/admin/auctions/${auctionSlug}/lots`);
            return response.data;
        } catch (error) {
            console.error('Error fetching auction lots:', error);
            throw error;
        }
    },

    // Admin: toggle featured status for a lot
    toggleLotFeatured: async (auctionSlug, lotSlug) => {
        try {
            const response = await api.post(`/admin/auctions/${auctionSlug}/lots/${lotSlug}/toggle-featured`);
            return response.data;
        } catch (error) {
            console.error('Error toggling lot featured status:', error);
            throw error;
        }
    },
};

export default auctionService; 